import { useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ClipboardCheck, Plus, Shield, Lock, Info } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const AdminAuditors = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", fullName: "" });
  const [saving, setSaving] = useState(false);
  const [grantFor, setGrantFor] = useState<{ user_id: string; full_name: string } | null>(null);

  const { data: restrictedDocs = [] } = useQuery({
    queryKey: ["restricted-docs"],
    queryFn: async () => (await (supabase as any).from("operating_documents").select("id, name, control_number, framework_ref").eq("is_restricted", true).eq("is_archived", false)).data ?? [],
  });
  const { data: grants = [], refetch: refetchGrants } = useQuery({
    queryKey: ["grants", grantFor?.user_id],
    enabled: !!grantFor,
    queryFn: async () => (await (supabase as any).from("restricted_document_grants").select("*").eq("auditor_user_id", grantFor!.user_id)).data ?? [],
  });

  const toggleGrant = async (docId: string, checked: boolean) => {
    if (!grantFor) return;
    if (checked) {
      const { error } = await (supabase as any).from("restricted_document_grants").insert({
        document_id: docId, auditor_user_id: grantFor.user_id, reason: "Granted by administrator",
      });
      if (error) return toast.error(error.message);
      toast.success("Access granted");
    } else {
      const { error } = await (supabase as any).from("restricted_document_grants").delete().eq("document_id", docId).eq("auditor_user_id", grantFor.user_id);
      if (error) return toast.error(error.message);
      toast.success("Access revoked");
    }
    refetchGrants();
  };

  const { data: company } = useQuery({
    queryKey: ["mi-company"],
    queryFn: async () => {
      const { data } = await supabase.from("companies").select("*").eq("name", "Marshall Islands Maritime Administrator").maybeSingle();
      return data;
    },
  });

  const { data: auditors = [] } = useQuery({
    queryKey: ["auditor-users"],
    queryFn: async () => {
      const { data: roles } = await supabase.from("user_roles").select("user_id").eq("role", "auditor" as any);
      if (!roles?.length) return [];
      const ids = roles.map((r: any) => r.user_id);
      const { data: profs } = await supabase.from("profiles").select("user_id, full_name, created_at").in("user_id", ids);
      return (profs || []).map((p: any) => ({ ...p, granted_at: p.created_at }));
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      if (!company?.id) throw new Error("Marshall Islands company not found");
      if (!/@register-iri\.com$/i.test(form.email.trim())) throw new Error("Auditor email must end with @register-iri.com");
      if (form.password.length < 8) throw new Error("Password must be at least 8 characters");
      const { data, error } = await supabase.functions.invoke("create-employee", {
        body: {
          email: form.email.trim(),
          password: form.password,
          fullName: form.fullName.trim(),
          companyId: company.id,
          role: "auditor",
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      return data;
    },
    onSuccess: () => {
      toast.success("Auditor account created");
      setOpen(false);
      setForm({ email: "", password: "", fullName: "" });
      qc.invalidateQueries({ queryKey: ["auditor-users"] });
    },
    onError: (e: any) => toast.error(e.message || "Failed to create auditor"),
  });

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-primary-foreground font-display flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-teal" /> ISM Auditor Accounts
          </h1>
          <p className="text-sm text-primary-foreground/50 mt-1">
            Marshall Islands Maritime Administrator — read-only access to Org Chart, Documents & SOPs, Maintenance/PMS and QHSE.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-teal text-navy-deep hover:bg-teal/90">
          <Plus className="w-4 h-4 mr-1" /> New Auditor
        </Button>
      </div>

      <Card className="bg-navy-deep border-primary-foreground/10">
        <CardHeader>
          <CardTitle className="text-primary-foreground text-base flex items-center gap-2">
            <Shield className="w-4 h-4 text-teal" /> Active Auditors ({auditors.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {auditors.length === 0 ? (
            <p className="text-primary-foreground/40 text-sm py-6 text-center">
              No auditors yet. Click "New Auditor" to create one.
            </p>
          ) : (
            <div className="space-y-2">
              {auditors.map((a: any) => (
                <div key={a.user_id} className="flex items-center justify-between p-3 rounded-lg bg-primary-foreground/5 border border-primary-foreground/10">
                  <div>
                    <p className="text-sm text-primary-foreground font-medium">{a.full_name || "—"}</p>
                    <p className="text-xs text-primary-foreground/40">Granted {a.granted_at ? new Date(a.granted_at).toLocaleDateString() : "—"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button asChild size="sm" variant="outline">
                      <Link to="/admin/users">Manage password</Link>
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setGrantFor({ user_id: a.user_id, full_name: a.full_name || "" })}>
                      <Lock className="w-3.5 h-3.5 mr-1" /> Restricted access
                    </Button>
                    <Badge className="bg-teal/20 text-teal border-teal/30">Auditor</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mt-3 p-3 rounded-lg bg-teal/5 border border-teal/20 text-xs text-primary-foreground/60 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 text-teal mt-0.5 flex-shrink-0" />
        <span>All password resets — for auditors, clients and employees — are managed from a single place: <Link to="/admin/users" className="text-teal underline">Admin → Users</Link>.</span>
      </div>



      {/* Restricted document grants dialog */}
      <Dialog open={!!grantFor} onOpenChange={(v) => !v && setGrantFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restricted document access — {grantFor?.full_name}</DialogTitle>
            <DialogDescription>
              Tick to grant this auditor view-only access to a restricted document (e.g. Ship Security Plan). Untick to revoke. All changes are recorded in the access log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 max-h-[50vh] overflow-auto">
            {(restrictedDocs as any[]).length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No restricted documents in the library.</p>}
            {(restrictedDocs as any[]).map((d) => {
              const granted = (grants as any[]).some((g) => g.document_id === d.id);
              return (
                <label key={d.id} className="flex items-start gap-3 p-3 rounded border border-primary-foreground/10 bg-primary-foreground/5 cursor-pointer">
                  <Checkbox checked={granted} onCheckedChange={(c) => toggleGrant(d.id, !!c)} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-primary-foreground font-medium">{d.control_number ? `${d.control_number} · ` : ""}{d.name}</p>
                    <p className="text-xs text-primary-foreground/40">{d.framework_ref || "—"}</p>
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantFor(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Marshall Islands Auditor</DialogTitle>
            <DialogDescription>Email must end with <strong>@register-iri.com</strong>. They will be prompted to change the password on first login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Full Name</Label>
              <Input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} placeholder="J. Surveyor" />
            </div>
            <div>
              <Label>Email (@register-iri.com)</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="auditor@register-iri.com" />
            </div>
            <div>
              <Label>Temporary Password</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 characters" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              disabled={saving || create.isPending}
              onClick={() => { setSaving(true); create.mutate(undefined, { onSettled: () => setSaving(false) }); }}
              className="bg-teal text-navy-deep hover:bg-teal/90"
            >
              {create.isPending ? "Creating…" : "Create Auditor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminAuditors;
